import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm } from './client'

/** IT Policies - `Easeedesk::it_policies` si capetele lui de citire. */

export interface PolicyCategory {
  ID: number
  category_name: string
}

export interface PolicyComment {
  id: number
  status: number
  state: string
  author_name: string
  created: string
  policy_name: string
  ticket_id: number
  comment: string
  decided_by_name: string
  decision_note: string
}

export interface PoliciesPage {
  title: string
  canEdit: boolean
  canDelete: boolean
  /** Manager IT al firmei, sau om de la furnizor. */
  canDecide: boolean
  categories: PolicyCategory[]
  commentStates: Record<string, string>
  comments: PolicyComment[]
}

export interface PolicyRow {
  ID: number
  subcategory: string
  category: string
  attachment: number
  has_nis2: boolean
  /** Documentul .doc se face tot in portal - e un fisier, nu un raspuns. */
  nis2_url: string
}

export interface PolicyList {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: PolicyRow[]
}

export interface PolicyAttachment {
  id: number
  file_name: string
  url: string
}

export interface PolicyContent {
  subcategory_id: number
  scope: string
  policy_id: number
  standard: string
  configuration: string
  exception: string
  attachments: PolicyAttachment[]
}

export interface PolicyNis2 {
  subcategory_id: number
  name: string
  nis2: string
  canEdit: boolean
  download: string
}

export const policiesPageQuery = queryOptions({
  queryKey: ['easeedesk', 'it-policies'],
  queryFn: () => apiGet<PoliciesPage>('/easeedesk/it-policies'),
})

/**
 * Lista, cu sortarea pe care o cere capul de tabel.
 *
 * `orderColumn` e indicele coloanei din `policiesModel`, acelasi pe care il
 * asteapta `DbalPoliciesSource::ORDER` (1 Subcategory, 2 Category, 3
 * Attachment). Era scris `'1'` aici fiindca ecranul avea o singura sageata de
 * sortare, pe Subcategory; capul unei liste le are pe toate.
 */
export function policyListQuery(
  search: string,
  category: number,
  start: number,
  length: number,
  orderColumn: number,
  orderDir: 'asc' | 'desc',
) {
  const p = new URLSearchParams({
    start: String(start),
    length: String(length),
    order_column: String(orderColumn),
    order_dir: orderDir,
  })
  if (search) p.set('search', search)
  if (category > 0) p.set('category', String(category))
  return queryOptions({
    queryKey: ['easeedesk', 'it-policies', 'list', search, category, start, length, orderColumn, orderDir],
    queryFn: () => apiGet<PolicyList>(`/easeedesk/it-policies/list?${p}`),
  })
}

export function policyContentQuery(subcategory: number, scope: 'provider' | 'company') {
  return queryOptions({
    queryKey: ['easeedesk', 'it-policies', subcategory, scope],
    queryFn: () => apiGet<PolicyContent>(`/easeedesk/it-policies/${subcategory}/content?scope=${scope}`),
  })
}

export function policyNis2Query(subcategory: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'it-policies', subcategory, 'nis2'],
    queryFn: () => apiGet<PolicyNis2>(`/easeedesk/it-policies/${subcategory}/nis2`),
  })
}

export function decidePolicyComment(comment: number, status: number, note: string) {
  return apiPost<{ ok: boolean; message?: string }>(`/easeedesk/it-policies/comments/${comment}/decide`, {
    status,
    note,
  })
}

/* -------------------------------------------------------------------------
 * SCRIERILE - `Policies::editConfigurationDetails`, `updateNis2`,
 * `addAttachment`, `deleteAttachment`, `add_comment`.
 * ---------------------------------------------------------------------- */

export type PolicyTextField = 'standard_details' | 'configuration_details' | 'exception_details'

export type PolicyScope = 'provider' | 'company'

/** Un singur camp pe apasare, ca cele trei formulare vechi. `scope` ca la citire: `company` = firma din bara. */
export function savePolicyText(subcategory: number, scope: PolicyScope, field: PolicyTextField, html: string) {
  return apiPost<{ status: string; policy: number }>(`/easeedesk/it-policies/${subcategory}/content`, { scope, [field]: html })
}

export function savePolicyNis2(subcategory: number, html: string) {
  return apiPost<{ status: string; policy: number }>(`/easeedesk/it-policies/${subcategory}/nis2`, { scope: 'company', nis2_content: html })
}

/** `filesToUpload[]`, ca formularul vechi, in multipart. */
export function attachPolicyFiles(subcategory: number, scope: PolicyScope, files: File[]) {
  const body = new FormData()
  body.append('scope', scope)
  for (const file of files) body.append('filesToUpload[]', file, file.name)

  return apiPostForm<{ status: string; message: string }>(`/easeedesk/it-policies/${subcategory}/attachments`, body)
}

export function removePolicyAttachment(attachment: number) {
  return apiPost<{ status: string; message: string }>(`/easeedesk/it-policies/attachments/${attachment}/remove`, {})
}

export function addPolicyComment(policy: number, ticket: number, comment: string) {
  return apiPost<{ status: string; message: string }>('/easeedesk/it-policies/comments', {
    policy_id: String(policy),
    ticket_id: String(ticket),
    comment,
  })
}

/* -------------------------------------------------------------------------
 * ROLURILE PE CATEGORIE - cine vede politicile unei categorii. Nimic bifat =
 * toata lumea, ca inainte. Cere `easeedesk.it_policies.edit`.
 * ---------------------------------------------------------------------- */

export interface PolicyAccessCategory {
  ID: number
  category_name: string
  /** Rolurile bifate; goala = toata lumea. */
  roles: number[]
}

export interface PolicyAccessPage {
  /** Tabela exista? Migrarea poate sa nu fi rulat inca. */
  ready: boolean
  roles: { id: number; name: string }[]
  categories: PolicyAccessCategory[]
}

export const policyAccessQuery = queryOptions({
  queryKey: ['easeedesk', 'it-policies', 'access'],
  queryFn: () => apiGet<PolicyAccessPage>('/easeedesk/it-policies/access'),
})

export function savePolicyCategoryRoles(category: number, roles: number[]) {
  return apiPost<{ ok: boolean; message?: string }>(`/easeedesk/it-policies/categories/${category}/roles`, { roles: roles.map(String) })
}
